import { SmsConversationEventRow } from "@/src/lib/types/tables.types";
import { ClosedReason } from "@/src/models/comms/enums";
import { SmsConversationEvent, smsConversationEventShape } from "@/src/models/comms/sms-conversation-event";
import { Group } from "@/src/models/group";
import { BaseRepository } from "@/src/repo/base-repository";

export const SMS_CONVERSATION_EVENT_TABLE_NAME = "sms_conversation_event";
export const fullSmsConversationEventSelect = `*`;

/**
 * Repository for the append-only SMS conversation status-transition log. Rows are
 * never mutated; new state is written via record_sms_conversation_transition()
 * (the atomic writer). This repo is for reads (metrics, history) plus the raw
 * insert path used by that writer / backfills.
 */
export class SmsConversationEventRepository extends BaseRepository<SmsConversationEvent, SmsConversationEventRow> {
  constructor() {
    super({
      tableName: SMS_CONVERSATION_EVENT_TABLE_NAME,
      selectClause: fullSmsConversationEventSelect,
      modelShape: smsConversationEventShape,
    });
  }

  /** Full transition history for a conversation, oldest first. */
  public async getByConversationId(conversationId: string, group: Group): Promise<SmsConversationEvent[]> {
    return this.getAll(
      group,
      { conditions: [{ field: "conversation_id", operator: "eq", value: conversationId }] },
      { field: "created_at", ascending: true }
    );
  }

  /** The earliest event-linked message for one workflow, used as its conversation-window boundary. */
  public async getFirstMessageEventByWorkflowId(
    workflowId: string,
    group: Group
  ): Promise<SmsConversationEvent | null> {
    const events = await this.getAll(
      group,
      {
        conditions: [
          { field: "workflow_id", operator: "eq", value: workflowId },
          { field: "message_id", operator: "isNot", value: null },
        ],
      },
      { field: "created_at", ascending: true }
    );

    return events[0] ?? null;
  }

  /**
   * Valerie booking evidence, including scheduling-outcome events, oldest first.
   * Generic appointment_booked means booked outside Valerie (including appointments merely
   * detected by the no-response sweep), regardless of actor type, and must not earn attribution.
   */
  public async getAppointmentBookedClosuresByPatientId(
    patientId: string,
    group: Group
  ): Promise<SmsConversationEvent[]> {
    const events = await this.getAll(
      group,
      { conditions: [{ field: "patient_id", operator: "eq", value: patientId }] },
      { field: "created_at", ascending: true }
    );

    return events.filter((event) => event.reason === ClosedReason.APPOINTMENT_BOOKED_BY_VALERIE);
  }
}

export const smsConversationEventRepository = new SmsConversationEventRepository();
export const getSmsConversationEvent = smsConversationEventRepository.get.bind(smsConversationEventRepository);
export const getAllSmsConversationEvents = smsConversationEventRepository.getAll.bind(smsConversationEventRepository);
export const getSmsConversationEventsByConversationId =
  smsConversationEventRepository.getByConversationId.bind(smsConversationEventRepository);
export const getFirstSmsMessageEventByWorkflowId =
  smsConversationEventRepository.getFirstMessageEventByWorkflowId.bind(smsConversationEventRepository);
export const getAppointmentBookedClosuresByPatientId =
  smsConversationEventRepository.getAppointmentBookedClosuresByPatientId.bind(smsConversationEventRepository);
export const insertSmsConversationEvent = smsConversationEventRepository.insert.bind(smsConversationEventRepository);
